import React, { useEffect } from "react";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import {
  Container,
  Typography,
  Button,
  Box,
  useTheme,
  useMediaQuery,
  Paper,
  Stack,
  CircularProgress,
  Avatar,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset"; 
import FormTextField from "../form/FormTextField";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { setAlert } from "../../features/slices/alertSlice";
import { useTranslation } from "react-i18next";
import { useUpdateProfileMutation } from "../../features/api/userApiSlice";

const ChangePassword = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down("sm"));

  const passwordSchema = yup.object().shape({
    currentPassword: yup.string().required(t("validation.currentpassword")),
    newPassword: yup
      .string()
      .min(8, t("validation.passwordtoo-short")) // Added basic length validation
      .required(t("validation.newpassword")),
    reEnterPassword: yup
      .string()
      .required(t("validation.reenterpassword"))
      .oneOf([yup.ref("newPassword"), null], t("validation.passwordnotmatch")),
  });

  const [updateProfile, { isLoading, isError, isSuccess, error }] =
    useUpdateProfileMutation();

  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(passwordSchema),
  });

  const submitPassword = async (data) => {
    let formData = {
      currentPassword: data?.currentPassword,
      password: data?.newPassword,
    };
    await updateProfile(formData).unwrap();
  };

  useEffect(() => {
    if (!isLoading && isSuccess) {
      dispatch(
        setAlert({
          isOpen: true,
          message: t("walletcard.changepasswordpage.successmessage"),
          variant: "success",
        })
      );
      reset();
    }
    if (isError) {
      dispatch(
        setAlert({
          isOpen: true,
          message: error?.data?.message || "An error occurred",
          variant: "error",
        })
      );
    }
  }, [isLoading, isSuccess, isError, dispatch, reset, t, error]);

  return (
    <Container sx={{
      py: {
        xs: 8, sm: 0, md: 0, lg: 0
      },
    }}>      
    {
        !mobileView && (
          <Typography fontWeight="bold" color={process.env.REACT_APP_COLOR_TEXT || "white"} sx={{mb: 3}}>
            {t("walletcard.changepassword")}
          </Typography>
        )
    }
    <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 5 },
          borderRadius: 4,
          backgroundColor: process.env.REACT_APP_IS_LIGHT_THEME === 'true' 
          ? 'rgba(0, 0, 0, 0.05)'  // darken for light theme
          : 'rgba(255, 255, 255, 0.05)', // lighten for dark theme
          border: "1px solid rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Header Section */}
        <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
          <Avatar
            sx={{
              bgcolor: "primary.main",
              width: 56,
              height: 56,
              mb: 2,
              boxShadow: `0 0 20px ${process.env.REACT_APP_COLOR_PRIMARY}`,
            }}
          >
            <LockResetIcon fontSize="large" />
          </Avatar>
          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{ color: process.env.REACT_APP_COLOR_TEXT || "white" }}
          >
            {t("walletcard.changepassword")}
          </Typography>
          {/* <Typography
            variant="body2"
            textAlign="center"
            sx={{ color: "text.secondary", mt: 1, opacity: 0.7 }}
          >
            {t("walletcard.changepasswordpage.subtitle") || "Ensure your account is using a long, random password to stay secure."}
          </Typography> */}
        </Box>

        <form onSubmit={handleSubmit(submitPassword)}>
          <Stack spacing={3}>
            <FormTextField
              label={t("walletcard.changepasswordpage.currentpassword")}
              control={control}
              controllerName="currentPassword"
              defaultValue={""}
              type="password"
              showPasswordToggle
              fullWidth
            />
            
            <FormTextField
              label={t("walletcard.changepasswordpage.newpassword")}
              control={control}
              controllerName="newPassword"
              defaultValue={""}
              type="password"
              showPasswordToggle
              fullWidth
            />

            <FormTextField
              label={t("walletcard.changepasswordpage.reenterpassword")}
              control={control}
              controllerName="reEnterPassword"
              defaultValue={""}
              type="password"
              showPasswordToggle
              fullWidth
            />

            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                type="submit"
                fullWidth
                size="large"
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: "bold",
                  fontSize: "1rem",
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t("walletcard.submit")
                )}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
};

export default ChangePassword;